import {
  apiUrl,
  ensureAccessToken,
  fetchSuperadminUsers as fetchUsers,
  type ApiConfig,
} from '../api';
import { state } from '../state';
import type { SuperadminUser } from '../types';
import { els, showToast, closeCreditAdjustmentModal, closeSubscriptionExtensionModal } from '../ui';
import {
  fetchAndPopulateUsers as fetchUsersData,
  formatUserLabel,
  getSelectedUserData as findSelectedUser,
} from './data';
import { renderUserCreditInfo as renderCreditInfo } from './render';
import {
  createHandleCreditAdjustmentSubmit,
  createHandleSubscriptionExtensionSubmit,
} from './handlers';

function populateDropdown(users: SuperadminUser[], previouslySelectedUserId: string | null): void {
  if (!els.superadminUserSelect) {
    return;
  }

  els.superadminUserSelect.innerHTML = '<option value="">All users</option>';
  users.forEach(user => {
    const opt = document.createElement('option');
    opt.value = user.userId || user.id || '';
    opt.textContent = formatUserLabel(user);
    els.superadminUserSelect?.appendChild(opt);
  });

  if (previouslySelectedUserId) {
    (els.superadminUserSelect as HTMLSelectElement).value = previouslySelectedUserId;
  }
}

async function refreshSuperadminUsers(cfg?: ApiConfig): Promise<SuperadminUser[]> {
  const previouslySelectedUserId = els.superadminUserSelect
    ? (els.superadminUserSelect as HTMLSelectElement).value
    : null;

  const users = await fetchUsersData({ fetchUsers, apiConfig: cfg });
  state.superadminUsers = users;
  populateDropdown(users, previouslySelectedUserId);
  const selectedUser = findSelectedUser(state.superadminUsers, previouslySelectedUserId);
  renderCreditInfo(selectedUser, els);
  return users;
}

export async function fetchAndPopulateUsers(cfg?: ApiConfig): Promise<SuperadminUser[]> {
  if (!(state.user && state.user.isSuperUser)) {
    return [];
  }
  if (!els.superadminUserSelect) {
    return [];
  }

  return refreshSuperadminUsers(cfg);
}

export function getSelectedUserData(): SuperadminUser | null {
  const selectedUserId = els.superadminUserSelect
    ? (els.superadminUserSelect as HTMLSelectElement).value
    : null;
  return findSelectedUser(state.superadminUsers, selectedUserId);
}

export function renderUserCreditInfo(): void {
  const userData = getSelectedUserData();
  renderCreditInfo(userData, els);
}

export const handleCreditAdjustmentSubmit = createHandleCreditAdjustmentSubmit({
  getSelectedUser: getSelectedUserData,
  getAdminName: () => state.user?.displayName || state.user?.name || state.user?.email || 'Admin',
  getCreditDelta: () => parseInt((els.creditDelta as HTMLInputElement)?.value || 'NaN', 10),
  getNote: () => (els.creditAdjustmentNote as HTMLTextAreaElement)?.value?.trim() || '',
  buildEndpoint: (userId: string) => apiUrl(`superadmin/users/${userId}/credits`),
  ensureAccessToken,
  refreshUsers: () => refreshSuperadminUsers(),
  onUsersUpdated: updatedUsers => {
    state.superadminUsers = updatedUsers;
  },
  renderUserCreditInfo: user => renderCreditInfo(user, els),
  closeModal: closeCreditAdjustmentModal,
  showToast,
});

export const handleSubscriptionExtensionSubmit = createHandleSubscriptionExtensionSubmit({
  getSelectedUser: getSelectedUserData,
  getAdminName: () => state.user?.displayName || state.user?.name || state.user?.email || 'Admin',
  getMonths: () => parseInt((els.subscriptionMonths as HTMLInputElement)?.value || '0', 10),
  getDays: () => parseInt((els.subscriptionDays as HTMLInputElement)?.value || '0', 10),
  getNote: () => (els.subscriptionExtensionNote as HTMLTextAreaElement)?.value?.trim() || '',
  buildEndpoint: (userId: string) => apiUrl(`superadmin/users/${userId}/subscription/extend`),
  ensureAccessToken,
  refreshUsers: () => refreshSuperadminUsers(),
  onUsersUpdated: updatedUsers => {
    state.superadminUsers = updatedUsers;
  },
  renderUserCreditInfo: user => renderCreditInfo(user, els),
  closeModal: closeSubscriptionExtensionModal,
  showToast,
});
